import { AFTER_LOGIN_REDIRECT, isBrowser } from "@constants/global";

export const FIRST_LOGIN_PARAM = "firstLogin";
export const FIRST_LOGIN_SESSION_KEY = "firstLoginToken";

/**
 * Only allow same-app relative paths. Blocks open redirects
 * (`//evil.com`, `https://…`, backslash tricks, etc.).
 */
export function isSafeInternalRedirect(
  target: unknown
): target is string {
  if (typeof target !== "string") return false;
  const value = target.trim();
  if (!value.startsWith("/")) return false;
  if (value.startsWith("//")) return false;
  if (value.includes("://")) return false;
  if (value.includes("\\")) return false;
  if (/[\u0000-\u001f]/.test(value)) return false;
  return true;
}

export function sanitizeInternalRedirect(
  target: unknown,
  fallback: string = AFTER_LOGIN_REDIRECT
): string {
  return isSafeInternalRedirect(target) ? target.trim() : fallback;
}

/** Build cross-subdomain first-login URL without putting the JWT in the query string. */
export function buildFirstLoginUrl(domain: string, token: string): string {
  const host = domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
  return `https://${host}/login#${FIRST_LOGIN_PARAM}=${encodeURIComponent(token)}`;
}

function readTokenFromSearch(search: string): string | null {
  try {
    return new URLSearchParams(search).get(FIRST_LOGIN_PARAM);
  } catch {
    return null;
  }
}

function readTokenFromHash(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  try {
    return new URLSearchParams(raw).get(FIRST_LOGIN_PARAM);
  } catch {
    return null;
  }
}

function readTokenFromLegacySource(source?: string | string[] | null): string | null {
  const value = Array.isArray(source) ? source[0] : source;
  if (!value || typeof value !== "string") return null;
  try {
    const decoded = decodeURIComponent(value);
    const match = decoded.match(/[?&#]firstLogin=([^&/#]+)/);
    if (match?.[1]) return decodeURIComponent(match[1]);
    const idx = decoded.indexOf("firstLogin=");
    if (idx >= 0) {
      return decodeURIComponent(
        decoded.slice(idx + "firstLogin=".length).split(/[&/#]/)[0]
      );
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Pull a one-time first-login JWT out of hash / query / session / legacy
 * `loginsource`, and scrub it from the address bar when possible.
 */
export function consumeFirstLoginToken(
  loginsource?: string | string[] | null
): string | null {
  if (!isBrowser) return null;

  const fromHash = readTokenFromHash(window.location.hash);
  if (fromHash) {
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
    sessionStorage.removeItem(FIRST_LOGIN_SESSION_KEY);
    return fromHash;
  }

  const url = new URL(window.location.href);
  const fromQuery = url.searchParams.get(FIRST_LOGIN_PARAM);
  if (fromQuery) {
    url.searchParams.delete(FIRST_LOGIN_PARAM);
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    sessionStorage.removeItem(FIRST_LOGIN_SESSION_KEY);
    return fromQuery;
  }

  const fromSession = sessionStorage.getItem(FIRST_LOGIN_SESSION_KEY);
  if (fromSession) {
    sessionStorage.removeItem(FIRST_LOGIN_SESSION_KEY);
    return fromSession;
  }

  return readTokenFromLegacySource(loginsource);
}

/**
 * If the current URL still carries `?firstLogin=…` (legacy), stash the token
 * in sessionStorage and strip it from the URL before navigating to /login.
 */
export function stashLegacyFirstLoginQuery(): boolean {
  if (!isBrowser) return false;
  const url = new URL(window.location.href);
  const token = url.searchParams.get(FIRST_LOGIN_PARAM);
  if (!token) return false;
  sessionStorage.setItem(FIRST_LOGIN_SESSION_KEY, token);
  url.searchParams.delete(FIRST_LOGIN_PARAM);
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  return true;
}

export function hasFirstLoginInLocation(): boolean {
  if (!isBrowser) return false;
  if (window.location.hash.includes(`${FIRST_LOGIN_PARAM}=`)) return true;
  if (readTokenFromSearch(window.location.search)) return true;
  if (sessionStorage.getItem(FIRST_LOGIN_SESSION_KEY)) return true;
  return false;
}
